/**
 * Module catalogue slice (PX-W6.5) — catalogue definitions, the picker's
 * project options, the selected project's attachment rows and the fetched
 * questionnaires.
 *
 * Canon: mutations dispatch → service → the `fulfilled` payload is applied;
 * NO reducer performs I/O. The activation toggle is deliberately NOT
 * optimistic (plan Decision 3): the row renders a per-row pending state, the
 * response row replaces the stored one on success, and on failure the old
 * value stays while an error banner renders — the client never re-decides
 * what the server already decided.
 */

import { createSelector, createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { selectActiveOrgId, type SessionState } from '@/features/auth/slices/sessionSlice'
import { isApiError, toApiError, type ApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'

import * as service from './services/moduleCatalogueService.http'
import type { ModuleDefinition, ModuleQuestionnaire, ProjectModule, ProjectOption } from './types'

/** Preserve the canonical ApiError (the transport already mapped it). */
const rejected = (error: unknown): ApiError => (isApiError(error) ? error : toApiError(error))

const orgKey = (getState: () => unknown): string | null =>
  selectActiveOrgId(getState() as { session: SessionState })

/** The attachment cache key — org-qualified so an org switch forces a refetch. */
const projectKey = (getState: () => unknown, projectId: string): string =>
  `${orgKey(getState) ?? 'none'}:${projectId}`

interface ProjectKeyed<T> {
  key: string
  value: T
}

/** The workspace catalogue (org-less; cached once per store). */
export const fetchDefinitions = createAsyncThunk<
  ModuleDefinition[],
  void,
  { rejectValue: ApiError }
>(
  'modules/catalogue/fetchDefinitions',
  async (_, { rejectWithValue }) => {
    try {
      return await service.moduleCatalogueService.listDefinitions()
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (_, { getState }) =>
      shouldFetch((getState() as { modules: ModuleCatalogueState }).modules.definitionsCache, null)
  }
)

/** The picker's project options. */
export const fetchModuleProjects = createAsyncThunk<
  ProjectOption[],
  void,
  { rejectValue: ApiError }
>(
  'modules/catalogue/fetchModuleProjects',
  async (_, { rejectWithValue }) => {
    try {
      return await service.moduleCatalogueService.listProjects()
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (_, { getState }) =>
      shouldFetch((getState() as { modules: ModuleCatalogueState }).modules.projectsCache, null)
  }
)

/** One project's attachment rows, keyed by `org:project`. */
export const fetchProjectModules = createAsyncThunk<
  ProjectKeyed<ProjectModule[]>,
  string,
  { rejectValue: ApiError }
>(
  'modules/catalogue/fetchProjectModules',
  async (projectId, { getState, rejectWithValue }) => {
    try {
      return {
        key: projectKey(getState, projectId),
        value: await service.moduleCatalogueService.listProjectModules(projectId)
      }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (projectId, { getState }) =>
      shouldFetch(
        (getState() as { modules: ModuleCatalogueState }).modules.projectCache,
        projectKey(getState, projectId)
      )
  }
)

/** One module's questionnaire, by lower-kebab key; fetched at most once. */
export const fetchQuestionnaire = createAsyncThunk<
  ProjectKeyed<ModuleQuestionnaire>,
  string,
  { rejectValue: ApiError }
>(
  'modules/catalogue/fetchQuestionnaire',
  async (key, { rejectWithValue }) => {
    try {
      return {
        key,
        value: await service.moduleCatalogueService.getQuestionnaire(key)
      }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    // The questionnaire is immutable reference data — once held, never refetch.
    condition: (key, { getState }) =>
      (getState() as { modules: ModuleCatalogueState }).modules.questionnaires[key] === undefined
  }
)

/**
 * Toggle one module's per-project activation. The PATCH body carries ONLY
 * `{enabled}` — nothing else is fabricated (AC-1).
 */
export const toggleModuleActivation = createAsyncThunk<
  ProjectModule,
  { projectId: string; moduleId: string; enabled: boolean },
  { rejectValue: ApiError }
>('modules/catalogue/toggleModuleActivation', async ({ projectId, moduleId, enabled }, { rejectWithValue }) => {
  try {
    return await service.moduleCatalogueService.patchProjectModule(projectId, moduleId, { enabled })
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

/**
 * Save the questionnaire answers. The PATCH carries the FULL `userAnswers`
 * map — the server replaces it wholesale, never merges (plan Decision 4) —
 * AND the row's CURRENT `enabled` value: `ProjectApplicationService.updateModule`
 * runs `target.setEnabled(patch.isEnabled())` UNCONDITIONALLY on a primitive
 * boolean, so a body omitting `enabled` would silently deactivate the module.
 * The automation contract test pins that Java line byte-for-byte.
 */
export const saveQuestionnaireAnswers = createAsyncThunk<
  ProjectModule,
  { projectId: string; moduleId: string; enabled: boolean; userAnswers: Record<string, unknown> },
  { rejectValue: ApiError }
>(
  'modules/catalogue/saveQuestionnaireAnswers',
  async ({ projectId, moduleId, enabled, userAnswers }, { rejectWithValue }) => {
    try {
      return await service.moduleCatalogueService.patchProjectModule(projectId, moduleId, {
        enabled,
        userAnswers
      })
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  }
)

export interface ModuleCatalogueState {
  definitions: ModuleDefinition[]
  definitionsCache: FetchCache
  projects: ProjectOption[]
  projectsCache: FetchCache
  projectModules: ProjectModule[]
  projectCache: FetchCache
  questionnaires: Record<string, ModuleQuestionnaire>
  /** The moduleId whose toggle is in flight (per-row pending state). */
  pendingModuleId: string | null
  savingAnswers: boolean
  error: ApiError | null
  mutationError: ApiError | null
}

const initialState: ModuleCatalogueState = {
  definitions: [],
  definitionsCache: initialFetchCache,
  projects: [],
  projectsCache: initialFetchCache,
  projectModules: [],
  projectCache: initialFetchCache,
  questionnaires: {},
  pendingModuleId: null,
  savingAnswers: false,
  error: null,
  mutationError: null
}

/** Replace the attachment row by id, or append when new. */
const upsertRow = (state: ModuleCatalogueState, row: ProjectModule): void => {
  const at = state.projectModules.findIndex((existing) => existing.id === row.id)
  if (at >= 0) state.projectModules[at] = row
  else state.projectModules.push(row)
}

export const moduleCatalogueSlice = createSlice({
  name: 'modules/catalogue',
  initialState,
  reducers: {
    moduleErrorDismissed(state) {
      state.error = null
      state.mutationError = null
    },
    modulesRetryClicked(state) {
      invalidate(state.definitionsCache)
      invalidate(state.projectsCache)
      invalidate(state.projectCache)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDefinitions.pending, (state) => {
        fetchStarted(state.definitionsCache)
        state.error = null
      })
      .addCase(fetchDefinitions.fulfilled, (state, action: PayloadAction<ModuleDefinition[]>) => {
        fetchSucceeded(state.definitionsCache, null)
        state.definitions = action.payload
      })
      .addCase(fetchDefinitions.rejected, (state, action) => {
        fetchFailed(state.definitionsCache)
        state.error = action.payload ?? null
      })
      .addCase(fetchModuleProjects.pending, (state) => {
        fetchStarted(state.projectsCache)
        state.error = null
      })
      .addCase(fetchModuleProjects.fulfilled, (state, action: PayloadAction<ProjectOption[]>) => {
        fetchSucceeded(state.projectsCache, null)
        state.projects = action.payload
      })
      .addCase(fetchModuleProjects.rejected, (state, action) => {
        fetchFailed(state.projectsCache)
        state.error = action.payload ?? null
      })
      .addCase(fetchProjectModules.pending, (state) => {
        fetchStarted(state.projectCache)
        state.error = null
      })
      .addCase(
        fetchProjectModules.fulfilled,
        (state, action: PayloadAction<ProjectKeyed<ProjectModule[]>>) => {
          fetchSucceeded(state.projectCache, action.payload.key)
          state.projectModules = action.payload.value
        }
      )
      .addCase(fetchProjectModules.rejected, (state, action) => {
        fetchFailed(state.projectCache)
        state.error = action.payload ?? null
      })
      .addCase(
        fetchQuestionnaire.fulfilled,
        (state, action: PayloadAction<ProjectKeyed<ModuleQuestionnaire>>) => {
          state.questionnaires[action.payload.key] = action.payload.value
        }
      )
      .addCase(fetchQuestionnaire.rejected, (state, action) => {
        state.error = action.payload ?? null
      })
      .addCase(toggleModuleActivation.pending, (state, action) => {
        state.pendingModuleId = action.meta.arg.moduleId
        state.mutationError = null
      })
      .addCase(toggleModuleActivation.fulfilled, (state, action: PayloadAction<ProjectModule>) => {
        state.pendingModuleId = null
        upsertRow(state, action.payload)
      })
      .addCase(toggleModuleActivation.rejected, (state, action) => {
        // The old `enabled` value stays — the row was never optimistically
        // changed, so "revert" is the absence of an update (AC-1).
        state.pendingModuleId = null
        state.mutationError = action.payload ?? null
      })
      .addCase(saveQuestionnaireAnswers.pending, (state) => {
        state.savingAnswers = true
        state.mutationError = null
      })
      .addCase(
        saveQuestionnaireAnswers.fulfilled,
        (state, action: PayloadAction<ProjectModule>) => {
          state.savingAnswers = false
          upsertRow(state, action.payload)
          // The row merge above is the response row; the invalidate ensures a
          // later access ALSO refetches the server's full truth (plan Step 2).
          invalidate(state.projectCache)
        }
      )
      .addCase(saveQuestionnaireAnswers.rejected, (state, action) => {
        state.savingAnswers = false
        state.mutationError = action.payload ?? null
      })
  }
})

export const { moduleErrorDismissed, modulesRetryClicked } = moduleCatalogueSlice.actions

/* ── Selectors ─────────────────────────────────────────────────────────── */

interface WithModules {
  modules: ModuleCatalogueState
}

export const selectModuleCatalogueState = (s: WithModules): ModuleCatalogueState => s.modules
export const selectModuleDefinitions = (s: WithModules): ModuleDefinition[] => s.modules.definitions
export const selectModuleProjects = (s: WithModules): ProjectOption[] => s.modules.projects
export const selectProjectModules = (s: WithModules): ProjectModule[] => s.modules.projectModules
export const selectPendingModuleId = (s: WithModules): string | null => s.modules.pendingModuleId
export const selectModuleMutationError = (s: WithModules): ApiError | null =>
  s.modules.mutationError

/** One attachment row by module id (the join key), or undefined. */
export const selectProjectModuleById = (s: WithModules, moduleId: string): ProjectModule | undefined =>
  s.modules.projectModules.find((row) => row.id === moduleId)

/**
 * The joined catalogue row: definition × attachment. A definition with no
 * attachment row renders as not-activated via a synthetic display-only row
 * (never dispatched — INV: the PATCH only fires for real rows the user
 * toggles, and the server creates the row on first PATCH).
 */
export interface JoinedModuleRow {
  definition: ModuleDefinition
  module: ProjectModule | null
}

export const selectJoinedModuleRows = createSelector(
  [selectModuleDefinitions, selectProjectModules],
  (definitions, projectModules): JoinedModuleRow[] =>
    definitions.map((definition) => ({
      definition,
      module: projectModules.find((row) => row.id === definition.moduleId) ?? null
    }))
)
